import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { photographerAPI } from '@services/photographerApi';
import { WatermarkSettings } from '@/components/dashboard/components/gallery';
import type { WatermarkConfig } from '@/types/watermark';
import { DEFAULT_WATERMARK_CONFIG } from '@/types/watermark';

const WatermarkSettingsPage: React.FC = () => {
  const [config, setConfig] = useState<WatermarkConfig>(DEFAULT_WATERMARK_CONFIG);
  const [photographerName, setPhotographerName] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reapplying, setReapplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await photographerAPI.getProfile();
        if (cancelled) return;
        const { brandingSettings, businessName } = res.data;
        setPhotographerName(businessName);
        if (brandingSettings?.watermarkConfig) {
          setConfig(brandingSettings.watermarkConfig);
        }
      } catch {
        // leave defaults in place
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await photographerAPI.updateWatermarkSettings(config);
      toast.success('Watermark settings saved');
    } catch {
      toast.error('Could not save watermark settings');
    } finally {
      setSaving(false);
    }
  };

  const reapply = async () => {
    setReapplying(true);
    try {
      await photographerAPI.updateWatermarkSettings(config);
      const res = await photographerAPI.reapplyWatermarkSettings();
      toast.success(
        `Queued ${res.data.queued} photo${res.data.queued === 1 ? '' : 's'} across ${res.data.eventsUpdated} event${res.data.eventsUpdated === 1 ? '' : 's'}`
      );
    } catch {
      toast.error('Could not queue existing photos for watermarking');
    } finally {
      setReapplying(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading watermark settings…</div>;
  }

  return (
    <div className="max-w-3xl space-y-8 p-6">
      <section>
        <h2 className="text-xl font-semibold text-gray-900">Watermark</h2>
        <p className="mt-1 text-sm text-gray-500">
          Non-purchased photos in your public galleries display this watermark. Leave the text
          empty to use the default <span className="font-mono">pixel-share.com</span> watermark.
        </p>
      </section>

      <WatermarkSettings
        config={config}
        onChange={setConfig}
        photographerName={photographerName}
      />

      <div className="flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={reapply}
          disabled={saving || reapplying}
          data-testid="watermark-reapply"
          className="rounded-md border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
        >
          {reapplying ? 'Queueing photos...' : 'Apply to existing photos'}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || reapplying}
          data-testid="watermark-save"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save watermark'}
        </button>
      </div>
    </div>
  );
};

export default WatermarkSettingsPage;
